import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, Trash2, Search, Shield, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuthStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import AdminHeader from '../components/admin/AdminHeader';

// 🎯 ФИКС NetworkError: Надежный фоллбек адрес бэкенда
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://fedyk-furniture-backend.onrender.com').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  wardrobes: 'Wardrobes',
  doors: 'Partitions & Doors',
  kitchens: 'Kitchens',
  other: 'Other',
};

const SOURCE_LABEL = {
  hero: 'Hero CTA',
  b2b: 'B2B Banner',
  header_contact: 'Header Contact',
  ai_concierge: 'AI Concierge',
  unknown: 'Other',
};

const RAW_TAGS_MAP_EN = {
  wood: 'Solid Wood', mdf: 'MDF Panels', chipboard: 'Chipboard', melamine: 'Melamine', acrylic: 'Acrylic',
  aluminum: 'Aluminum Profile', glass: 'Premium Glass', mirror: 'Mirror', stone: 'Natural Stone',
  natural_veneer: 'Natural Veneer', engineered_veneer: 'Modified Veneer', hpl: 'HPL Panels',
  leather: 'Natural Leather', eco_leather: 'Eco Leather', fabric: 'Fabric / Textiles',
  primer: 'Primer', paint: 'Paint', lacquer: 'Lacquer',
  matte: 'Matte', glossy: 'Glossy', satin: 'Satin', textured: 'Textured', natural: 'Natural', brushed: 'Brushed'
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
};

const flagEmoji = (code) => {
  if (!code || typeof code !== 'string' || code.trim().length !== 2) return '';
  try {
    return String.fromCodePoint(...code.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0)));
  } catch (e) {
    return '';
  }
};

const AdminLoginPage = () => {
  const { isAuthenticated, user, login } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 🛡️ Состояния 2FA
  const [requires2FA, setRequires2FA] = useState(false);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const otpInputsRef = useRef([]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      navigate('/admin/leads', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Обработка таймера повторной отправки
  useEffect(() => {
    let interval = null;
    if (requires2FA && resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
      if (interval) clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [requires2FA, resendTimer]);

  // Фокус на первую ячейку OTP
  useEffect(() => {
    if (requires2FA && otpInputsRef.current[0]) {
      otpInputsRef.current[0].focus();
    }
  }, [requires2FA]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);

    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setOtpCode(pastedData.split(''));
      otpInputsRef.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fullOtp = otpCode.join('');
      const response = await login(email, password, fullOtp || null);

      if (response && response.status === '2fa_required') {
        setRequires2FA(true);
        setResendTimer(60);
        setCanResend(false);
        toast.info('Security verification code sent to Telegram & Email');
        return;
      }

      const u = useAuthStore.getState().user;
      if (u?.role !== 'admin') {
        useAuthStore.getState().logout();
        toast.error('This account does not have admin access');
        return;
      }

      toast.success('Welcome back');
      const next = location.state?.from || '/admin/leads';
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    setCanResend(false);
    setResendTimer(60);
    setOtpCode(['', '', '', '', '', '']);
    try {
      const response = await login(email, password, null);
      if (response && response.status === '2fa_required') {
        toast.success('A new 6-digit code has been dispatched');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to resend code');
      setCanResend(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 text-left font-light">
      <SEO title="Admin · FEDYK Furniture" description="Restricted area" path="admin" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm bg-white border border-neutral-200 p-8 shadow-sm"
      >
        <Link to="/en" className="block mb-8 text-center">
          <span className="font-heading text-2xl font-light tracking-tight text-black">
            FEDYK<span className="font-extralight text-neutral-500"> Furniture</span>
          </span>
        </Link>
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-neutral-400 text-center mb-2">Security Perimeter</p>
        <h1 className="text-2xl font-extralight tracking-tight text-center text-black mb-6">Admin Control Console</h1>

        {!requires2FA ? (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <Input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="h-12 rounded-none border-neutral-200 focus:border-black bg-white text-sm"
              data-testid="admin-email-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-12 rounded-none border-neutral-200 focus:border-black bg-white text-sm"
              data-testid="admin-password-input"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-black text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-medium transition-colors"
              data-testid="admin-submit-btn"
            >
              {submitting ? 'Authenticating…' : 'Sign In'}
            </Button>
          </form>
        ) : (
          /* 🛡️ ШАГ 2: ВВОД 2FA OTP КОДА */
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="text-center mb-4">
              <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-2">
                <Shield className="w-5 h-5 text-black" />
              </div>
              <p className="text-xs text-neutral-500">
                Enter the 6-digit code sent to Telegram & Email
              </p>
            </div>

            <div className="flex justify-between gap-1.5" onPaste={handlePaste}>
              {otpCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-10 h-12 text-center text-lg font-mono text-black border border-neutral-200 focus:border-black outline-none rounded-none bg-white transition-all"
                />
              ))}
            </div>

            <Button
              type="submit"
              disabled={submitting || otpCode.join('').length < 6}
              className="w-full h-12 bg-black text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-medium transition-colors"
            >
              {submitting ? 'Verifying...' : 'Verify & Enter'}
              <KeyRound className="ml-2 w-4 h-4" />
            </Button>

            <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setOtpCode(['', '', '', '', '', '']);
                }}
                className="text-xs text-neutral-400 hover:text-black flex items-center gap-1 font-mono"
              >
                <ArrowLeft className="w-3 h-3" />
                <span>Back</span>
              </button>

              {canResend ? (
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="text-xs text-black hover:underline flex items-center gap-1 font-mono"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Resend Code</span>
                </button>
              ) : (
                <span className="text-xs text-neutral-400 font-mono">
                  Resend in <strong className="text-black">{resendTimer}s</strong>
                </span>
              )}
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

const LeadsDashboardPage = () => {
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSource, setFilterSource] = useState('');

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/leads' } });
    }
  }, [hydrated, navigate]);

  const fetchLeads = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterSource) params.append('source', filterSource);
      const res = await fetch(`${API}/admin/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        navigate('/admin', { replace: true });
        return;
      }
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (err) {
      toast.error('Failed to load leads');
      setLeads([]); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && user?.role === 'admin') fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filterCategory, filterSource]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      const res = await fetch(`${API}/admin/leads/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success('Lead removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (l.name || '').toLowerCase().includes(q) ||
      (l.phone || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.contact_value || '').toLowerCase().includes(q)
    );
  });

  const contactDisplay = (lead) => {
    const parts = [];
    if (lead.phone) parts.push(lead.phone);
    if (lead.email) parts.push(lead.email);
    if (lead.contact_value) parts.push(`${lead.contact_type || 'contact'}: ${lead.contact_value}`);
    return parts.length ? parts.join(' · ') : '—';
  };

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-xs font-mono text-neutral-400 bg-white">Loading pipeline core…</div>;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-left font-light">
      <SEO title="Leads · Admin" description="FEDYK Furniture leads dashboard" path="admin/leads" />
      
      <AdminHeader active="leads" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-neutral-200 pb-6">
          <div>
            <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">Mini-CRM · Pipeline</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">Incoming Leads</h1>
            <p className="text-sm font-light text-neutral-500 mt-4 max-w-xl leading-relaxed">
              Отслеживайте входящие заявки, контекстные конфигурации ИИ-консьержа и спецификации из премиального лукбука.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs font-mono text-neutral-400 bg-neutral-100 border border-neutral-200 px-3 py-2">
              {filteredLeads.length} of {leads.length} Active Records
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchLeads} 
              disabled={loading} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium uppercase tracking-wider"
              style={{ height: '40px' }}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} /> Refresh Stream
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-10 shadow-[0_2px_8px_rgba(0,0,0,0.01)]">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search leads by token, client name, phone or email index…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-12 rounded-none bg-white text-neutral-900 placeholder:text-neutral-400 font-light focus-visible:border-black"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="h-11 px-4 border border-neutral-200 bg-white text-sm min-w-[180px] text-neutral-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
            data-testid="admin-filter-category"
          >
            <option value="">All system categories</option>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="h-11 px-4 border border-neutral-200 bg-white text-sm min-w-[180px] text-neutral-900 focus:outline-none focus:border-black rounded-none cursor-pointer font-light"
            data-testid="admin-filter-source"
          >
            <option value="">All engine sources</option>
            {Object.entries(SOURCE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-24 text-center text-neutral-400 text-xs font-mono">Querying live stream nodes…</div>
        ) : filteredLeads.length === 0 ? (
          <div className="py-24 text-center text-neutral-500 border border-neutral-200 bg-white rounded-none shadow-sm text-sm font-light">
            No active lead records match the chosen filter configuration.
          </div>
        ) : (
          <div className="border border-neutral-200 bg-white shadow-sm overflow-x-auto rounded-none" data-testid="admin-leads-table">
            <table className="w-full text-sm font-light text-neutral-600">
              <thead>
                <tr className="text-left text-[10px] tracking-wider uppercase font-mono text-neutral-400 bg-neutral-50 border-b border-neutral-200 select-none">
                  <th className="px-5 py-4 font-semibold">Timestamp</th>
                  <th className="px-5 py-4 font-semibold">Client Name</th>
                  <th className="px-5 py-4 font-semibold">Requested System / Custom Configuration</th>
                  <th className="px-5 py-4 font-semibold">Channels</th>
                  <th className="px-5 py-4 font-semibold">Country</th>
                  <th className="px-5 py-4 font-semibold">Scope</th>
                  <th className="px-5 py-4 font-semibold">Trigger</th>
                  <th className="px-5 py-4 font-semibold w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filteredLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-neutral-50/40 transition-colors"
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="px-5 py-4 text-neutral-400 font-mono text-xs whitespace-nowrap">{formatDate(lead.created_at)}</td>
                    <td className="px-5 py-4 font-normal text-neutral-900 whitespace-nowrap">{lead.name || '—'}</td>
                    <td className="px-5 py-4 text-left">
                      <div className="font-normal text-neutral-900">{CATEGORY_LABEL[lead.category] || lead.category || '—'}</div>
                      
                      {/* Сценарий 1: Прямой лид с выбранным материалом основы или финишем */}
                      {(lead.selected_material || lead.selected_finish) && (
                        <div className="mt-2 space-y-1 text-xs text-neutral-600 bg-neutral-50 p-2.5 border border-neutral-200 font-light max-w-xs shadow-[inset_0_1px_2px_rgba(0,0,0,0.01)]">
                          {lead.selected_material && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-neutral-400 font-mono text-[9px] uppercase tracking-wider">Material:</span>
                              <span className="text-neutral-900 font-normal">{RAW_TAGS_MAP_EN[lead.selected_material] || lead.selected_material}</span>
                            </div>
                          )}
                          {lead.selected_finish && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-neutral-400 font-mono text-[9px] uppercase tracking-wider">Surface:</span>
                              <span className="text-neutral-900 font-normal">{RAW_TAGS_MAP_EN[lead.selected_finish] || lead.selected_finish}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Сценарий 2: Комплексная корзина спецификации с массивом изделий */}
                      {lead.items && Array.isArray(lead.items) && lead.items.map((item, idx) => (
                        <div key={idx} className="mt-2 text-xs border-l-2 border-neutral-300 pl-3 py-0.5 max-w-sm">
                          <span className="font-normal text-neutral-900">{item.name}</span> <span className="text-neutral-400 font-mono text-[10px]">[{item.sku}]</span>
                          {(item.selected_material || item.selected_finish) && (
                            <div className="text-[11px] text-neutral-500 mt-1 font-light bg-neutral-50/60 px-2 py-1 border border-neutral-100">
                              {item.selected_material && `Mat: ${RAW_TAGS_MAP_EN[item.selected_material] || item.selected_material}`}
                              {item.selected_material && item.selected_finish && ' · '}
                              {item.selected_finish && `Fin: ${RAW_TAGS_MAP_EN[item.selected_finish] || item.selected_finish}`}
                            </div>
                          )}
                        </div>
                      ))}
                    </td>

                    <td className="px-5 py-4 font-light text-neutral-600 text-xs leading-relaxed max-w-xs break-all">{contactDisplay(lead)}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-900 font-medium">
                      <span className="mr-2 text-base align-middle inline-block select-none">{flagEmoji(lead.country)}</span>
                      <span className="align-middle uppercase font-mono text-xs">{lead.country || '—'}</span>
                    </td>
                    <td className="px-5 py-4 uppercase font-mono text-xs text-neutral-400">{lead.language || '—'}</td>
                    <td className="px-5 py-4 text-xs font-mono text-neutral-400 whitespace-nowrap">{SOURCE_LABEL[lead.source] || lead.source || '—'}</td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => handleDelete(lead.id)}
                        className="text-neutral-400 hover:text-red-600 p-1 transition-colors rounded-none"
                        title="Delete lead archive permanently"
                        data-testid={`lead-delete-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export { AdminLoginPage, LeadsDashboardPage };
